import React from "react";
import PropTypes from "prop-types";
import { Checkbox } from "primereact/checkbox";
import { Label, Dropdown, InputText } from "../../../../components/Inputs";
import {
  JarvisConnectionPermissionOptions,
  SiSTelekomPermission,
} from "../Utils";
import { validateEmail } from "../../../../components/Utils";

const getEmptyModel = () => ({
  parent_company_id: 0,
  parentCompanyName: "",
  permissions: [],
  isSISTelekomHerbeiruf: false,
  externalEmail: "",
});

function generateState(pModel, kdlCompanyList = []) {
  const model = pModel || {};
  let permissions = "";
  if (model.permissions) {
    const tokens = model.permissions.split(", ");
    permissions = tokens[0];
  }
  return {
    parent_company_id: model.parent_company_id || 0,
    parentCompanyName: kdlCompanyList.length > 0 ? kdlCompanyList[0].name : "",
    permissions,
    isSISTelekomHerbeiruf: model.isSISTelekomHerbeiruf === 1,
    externalEmail: model.externalEmail || "",
    error: {
      parent_company_id: null,
      permissions: null,
    },
  };
}

function JarvisConnectionForm({
  forwardedRef,
  className,
  kdlCompanyList,
  isContractor,
  defaultExternalEmail,
  readOnly,
  isDL,
  model,
}) {
  const [state, setState] = React.useState(() =>
    generateState(model, kdlCompanyList),
  );

  React.useEffect(() => {
    setState(generateState(model, kdlCompanyList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, kdlCompanyList]);

  const validate = React.useCallback(
    (initialize) => {
      const { error, ...modelState } = state;
      let isValid = true;
      const newError = { ...error };
      Object.keys(modelState).forEach((key) => {
        if (newError[key] !== undefined) {
          if (key === "permissions") {
            if (!modelState[key] || modelState[key].length === 0) {
              newError[key] = true;
              isValid = false;
            }
          } else if (modelState[key] === "") {
            newError[key] = true;
            isValid = false;
          }
        }
      });
      // externalEmail is required if isSISTelekomHerbeiruf is true
      if (modelState.isSISTelekomHerbeiruf) {
        if (
          !modelState.externalEmail ||
          modelState.externalEmail.trim() === "" ||
          !validateEmail(modelState.externalEmail)
        ) {
          newError.externalEmail = true;
          isValid = false;
        } else {
          newError.externalEmail = null;
        }
      }
      setState((prev) => ({ ...prev, error: newError }));
      if (!isValid) {
        return null;
      }
      if (initialize) {
        setState(getEmptyModel());
      }
      const { id, index, status, status_id } = model;
      return {
        ...modelState,
        isSISTelekomHerbeiruf: modelState.isSISTelekomHerbeiruf ? 1 : 0,
        id,
        index,
        status,
        status_id,
        oldStatusId: status_id,
      };
    },
    [state, model],
  );

  const getModel = React.useCallback(
    (initialize = false) => {
      return validate(initialize);
    },
    [validate],
  );

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      getModel,
    }),
    [getModel],
  );

  const setPermissions = React.useCallback((permissions) => {
    setState((prev) => ({
      ...prev,
      permissions,
      error: {
        ...prev.error,
        permissions: permissions === "",
      },
    }));
  }, []);

  const onSISTelekomChange = React.useCallback(
    (event) => {
      setState((prev) => {
        // const externalEmail = prev.externalEmail !== "" ? prev.externalEmail : defaultExternalEmail || "";
        let externalEmail = "";
        if (event.checked) {
          externalEmail =
            model.externalEmail && model.externalEmail !== ""
              ? model.externalEmail
              : defaultExternalEmail;
        }
        console.log(
          "onSISTelekomChange:",
          event.checked,
          externalEmail,
          defaultExternalEmail,
        );
        return {
          ...prev,
          isSISTelekomHerbeiruf: event.checked,
          externalEmail,
        };
      });
    },
    [defaultExternalEmail, model],
  );

  const onChangeExternalEmail = React.useCallback((email) => {
    setState((prev) => ({
      ...prev,
      externalEmail: email,
    }));
  }, []);

  const {
    parentCompanyName,
    permissions,
    isSISTelekomHerbeiruf,
    externalEmail,
    error,
  } = state;
  const KDLCompanyOptions = kdlCompanyList.map((item) => ({
    label: item.name,
    value: `${item.id}`,
  }));

  const permissionOptions = React.useMemo(
    () =>
      JarvisConnectionPermissionOptions.filter((option) => {
        /* if (isContractor) {
          return option.type === 1;
        } else  */
        if (isDL) {
          return option.label === "NE4";
        } else if (isSISTelekomHerbeiruf) {
          return option.label === "NE3";
        }
        return true;
      }),
    [/* isContractor,  */isSISTelekomHerbeiruf, isDL],
  );

  // React.useEffect(() => {
  //   if (permissions !== "") {
  //     const foundPermission = permissionOptions.find(option => option.value === permissions);
  //     console.log(">> Checking permissions validity:", permissions, foundPermission, permissionOptions);
  //     if (permissions && !foundPermission) {
  //       setPermissions("");
  //     }
  //   }
  // }, [permissions, setPermissions, permissionOptions]);

  return (
    <div className={className} style={{ minHeight: "288px" }}>
      <div
        className={`form-group-item ${
          error.parentCompanyName ? "error-group" : ""
        }`}
      >
        <Label htmlFor="parentCompanyName">{`Vertragspartner*`}</Label>
        <Dropdown
          id="parentCompanyName"
          className="w3-block w3-border"
          value={parentCompanyName}
          placeholder="Bitte auswählen"
          options={KDLCompanyOptions}
          readOnly
        />
      </div>
      <div
        className={`form-group-item ${error.permissions ? "error-group" : ""}`}
      >
        <Label htmlFor="permissions">{`Rechte*`}</Label>
        <Dropdown
          id="permissions"
          className="w3-block w3-border"
          value={permissions}
          placeholder="Bitte auswählen"
          options={permissionOptions}
          onChange={(event) => setPermissions(event.value)}
          readOnly={readOnly}
        />
      </div>
      {!isContractor && permissions === SiSTelekomPermission && (
        <div className="margin-top">
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Checkbox
              inputId={`sisTelekom`}
              checked={isSISTelekomHerbeiruf}
              style={{ cursor: "default" }}
              onChange={onSISTelekomChange}
              disabled={readOnly}
            ></Checkbox>
            <label htmlFor={`sisTelekom`} className="bold">
              {"SiS Telekom Herbeiruf"}
            </label>
          </div>
        </div>
      )}
      {permissions === SiSTelekomPermission && !isSISTelekomHerbeiruf && (
        <div className="form-group-item margin-top" />
      )}
      {permissions === SiSTelekomPermission && isSISTelekomHerbeiruf && (
        <div
          className={`form-group-item margin-top ${
            error.externalEmail ? "error-group" : ""
          }`}
        >
          <Label htmlFor="externalEmail">{`Externe E-Mail-Adresse*`}</Label>
          <InputText
            id="externalEmail"
            className={`w3-block w3-border${readOnly ? " w3-round pad" : ""}`}
            value={externalEmail}
            onChange={(event) => onChangeExternalEmail(event.target.value)}
            readonly={readOnly}
          />
          <div className="pad-tb w3-medium w3-text-grey">
            {"Wird für die Kommunikation mit der Telekom genutzt"}
          </div>
        </div>
      )}
    </div>
  );
}

JarvisConnectionForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    parent_company_id: PropTypes.number,
    permissions: PropTypes.string,
    isSISTelekomHerbeiruf: PropTypes.number,
    externalEmail: PropTypes.string,
  }),
  kdlCompanyList: PropTypes.array,
  // isContractor: PropTypes.bool.isRequired,
  defaultExternalEmail: PropTypes.string,
  readOnly: PropTypes.bool,
  isDL: PropTypes.bool,
};

JarvisConnectionForm.defaultProps = {
  className: "",
  model: {
    parent_company_id: 0,
    permissions: "",
    isSISTelekomHerbeiruf: 0,
    externalEmail: "",
  },
  companyList: [],
  kdlCompanyList: [],
  defaultExternalEmail: "",
  isDL: false,
};

export default React.forwardRef((props, ref) => {
  return <JarvisConnectionForm {...props} forwardedRef={ref} />;
});
